// ─── Attack Paths Explorer ──────────────────────────────────────────────
// Table-based view of org-level attack paths.
// Shows source → target paths with deterministic risk scores.

import { useAttackPaths } from '@/hooks/use-attack-paths';
import { ATTACK_PATH_SCORE_COLORS, ATTACK_PATH_SCORE_LABELS, getScoreLevel } from '@/lib/constants';
import { useState } from 'react';

export function AttackPathsPage() {
	const [minScore, setMinScore] = useState(0);
	const [expandedId, setExpandedId] = useState<string | null>(null);

	const { data, isLoading } = useAttackPaths({ minScore, limit: 50 });

	if (isLoading) {
		return <div className="text-muted-foreground">Loading attack paths...</div>;
	}

	const paths = data?.paths ?? [];
	const summary = data?.summary;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="space-y-1">
				<h1 className="text-2xl font-bold text-foreground">Attack Paths</h1>
				<p className="text-sm text-muted-foreground">
					Exploitable paths from attacker-controlled inputs to sensitive targets in your CI/CD trust
					graph.
				</p>
			</div>

			{/* Summary cards */}
			{summary && (
				<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
					<SummaryCard label="Total Paths" value={summary.total} />
					<SummaryCard label="Critical" value={summary.criticalCount} className="text-red-600" />
					<SummaryCard label="High" value={summary.highCount} className="text-orange-600" />
					<SummaryCard
						label="Max Score"
						value={summary.maxScore.toFixed(1)}
						className="text-foreground"
					/>
				</div>
			)}

			{/* Filter */}
			<div className="flex items-center gap-4">
				<label className="text-sm text-muted-foreground">
					Minimum score:
					<select
						value={minScore}
						onChange={(e) => setMinScore(Number(e.target.value))}
						className="ml-2 rounded border border-border bg-background px-2 py-1 text-sm"
					>
						<option value={0}>All</option>
						<option value={2}>≥ 2 (Medium+)</option>
						<option value={4}>≥ 4 (High+)</option>
						<option value={7}>≥ 7 (Critical)</option>
					</select>
				</label>
			</div>

			{/* Paths table */}
			{paths.length === 0 ? (
				<div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
					No attack paths found. {minScore > 0 && 'Try lowering the minimum score filter.'}
				</div>
			) : (
				<div className="rounded-lg border border-border bg-card">
					<table className="w-full">
						<thead>
							<tr className="border-b border-border text-left text-sm text-muted-foreground">
								<th className="px-4 py-3 font-medium">Score</th>
								<th className="px-4 py-3 font-medium">Source</th>
								<th className="px-4 py-3 font-medium">Target</th>
								<th className="px-4 py-3 font-medium">Steps</th>
								<th className="px-4 py-3 font-medium">Boundaries</th>
							</tr>
						</thead>
						<tbody>
							{paths.map((path) => {
								const level = getScoreLevel(path.score);
								const isExpanded = expandedId === path.id;
								return (
									<PathRow
										key={path.id}
										path={path}
										level={level}
										isExpanded={isExpanded}
										onToggle={() => setExpandedId(isExpanded ? null : path.id)}
									/>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}

function SummaryCard({
	label,
	value,
	className,
}: {
	label: string;
	value: number | string;
	className?: string;
}) {
	return (
		<div className="rounded-lg border border-border bg-card p-4">
			<div className="text-sm text-muted-foreground">{label}</div>
			<div className={`mt-1 text-2xl font-bold ${className ?? 'text-foreground'}`}>{value}</div>
		</div>
	);
}

function PathRow({
	path,
	level,
	isExpanded,
	onToggle,
}: {
	path: {
		id: string;
		source: { label: string; nodeType: string };
		target: { label: string; nodeType: string };
		score: number;
		stepCount: number;
		boundaryCount: number;
		factors: ReadonlyArray<{ factor: string; weight: number; description: string }>;
		steps: ReadonlyArray<{
			nodeId: string;
			nodeType: string;
			label: string;
			edgeType: string;
			crossesBoundary: boolean;
		}>;
	};
	level: string;
	isExpanded: boolean;
	onToggle: () => void;
}) {
	const colorClass = ATTACK_PATH_SCORE_COLORS[level] ?? 'bg-gray-100 text-gray-800';
	const levelLabel = ATTACK_PATH_SCORE_LABELS[level] ?? level;

	return (
		<>
			<tr
				className="cursor-pointer border-b border-border hover:bg-accent/50"
				onClick={onToggle}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						onToggle();
					}
				}}
			>
				<td className="px-4 py-3">
					<span
						className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${colorClass}`}
					>
						{path.score.toFixed(1)} — {levelLabel}
					</span>
				</td>
				<td className="px-4 py-3">
					<div className="text-sm font-medium text-foreground">{path.source.label}</div>
					<div className="text-xs text-muted-foreground">{path.source.nodeType}</div>
				</td>
				<td className="px-4 py-3">
					<div className="text-sm font-medium text-foreground">{path.target.label}</div>
					<div className="text-xs text-muted-foreground">{path.target.nodeType}</div>
				</td>
				<td className="px-4 py-3 text-sm text-foreground">{path.stepCount}</td>
				<td className="px-4 py-3 text-sm text-foreground">{path.boundaryCount}</td>
			</tr>

			{/* Expanded detail row */}
			{isExpanded && (
				<tr className="border-b border-border bg-accent/30">
					<td colSpan={5} className="px-4 py-4">
						<div className="space-y-4">
							{/* Scoring factors */}
							<div>
								<h4 className="text-xs font-semibold uppercase text-muted-foreground">
									Scoring Factors
								</h4>
								<div className="mt-2 space-y-1">
									{path.factors.map((f, i) => (
										<div key={`factor-${String(i)}`} className="flex items-center gap-2 text-sm">
											<span className="font-mono text-xs text-muted-foreground">
												+{f.weight.toFixed(1)}
											</span>
											<span className="text-foreground">{f.description}</span>
										</div>
									))}
								</div>
							</div>

							{/* Path steps */}
							{path.steps.length > 0 && (
								<div>
									<h4 className="text-xs font-semibold uppercase text-muted-foreground">
										Path Steps
									</h4>
									<div className="mt-2 flex flex-wrap items-center gap-1 text-sm">
										<span className="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800">
											{path.source.label}
										</span>
										{path.steps.map((s, i) => (
											<span key={`step-${String(i)}`} className="flex items-center gap-1">
												<span className="text-muted-foreground">→</span>
												<span
													className={`rounded px-2 py-0.5 text-xs ${
														s.crossesBoundary
															? 'bg-red-100 text-red-800'
															: 'bg-gray-100 text-gray-800'
													}`}
												>
													{s.label}
												</span>
											</span>
										))}
										<span className="text-muted-foreground">→</span>
										<span className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-800">
											{path.target.label}
										</span>
									</div>
								</div>
							)}
						</div>
					</td>
				</tr>
			)}
		</>
	);
}
